/**
 * The trades terminal: the account's money in one line, the selected cycle on
 * a large chart, and every open cycle as a row with its price rail.
 *
 * The reader's question is where each cycle stands against what it paid, its
 * target and its next buy. The rail answers it for the whole list at once;
 * the chart answers it for one cycle in full, with each price tagged on the
 * axis. Nothing here closes a position: the bot does that, never at a loss.
 */
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/design-system/Button";
import { poolColour } from "@/design-system/poolColour";
import { StatusBadge } from "@/design-system/StatusBadge";
import type { OverviewResponse, SlotResponse } from "@/shared/api";
import { CycleChart } from "@/shared/cycles/CycleChart";
import { delistingText } from "@/shared/cycles/delisting";
import { slotQuote } from "@/shared/cycles/quote";
import { compareDecimal, isNegative, isZero, money, signedMoney, signedPercent } from "@/shared/money/money";
import { logicalPoolOf, returnOf } from "@/shared/strategy/versions";
import { humanAge } from "@/shared/time/age";

import { CycleRail } from "./CycleRail";

type Filter = "ALL" | "PRIME" | "PULSE" | "FLASH" | "TRAILING" | "DOWN";

const FILTERS: ReadonlyArray<readonly [Filter, string]> = [
  ["ALL", "Все"],
  ["PRIME", "PRIME"],
  ["PULSE", "PULSE"],
  ["FLASH", "FLASH"],
  ["TRAILING", "В трейлинге"],
  ["DOWN", "В минусе"],
];

/** In profit, its exit armed: the bot follows the price up and sells on the
 *  turn (a client asked to see these, 2026-10-29). */
function trailing(slot: SlotResponse): boolean {
  return slot.outlook?.trailing_sell_price != null;
}

function tone(value: string | null | undefined): string {
  if (value == null || isZero(value)) return "text-text-secondary";
  return isNegative(value) ? "text-status-negative" : "text-status-positive";
}

function ageOf(slot: SlotResponse, now: number): string {
  if (slot.opened_at === null) return "—";
  return humanAge((now - Date.parse(slot.opened_at)) / 1000);
}

function ladder(slot: SlotResponse): string | null {
  const placement = slot.evidence?.placement;
  if (placement == null) return null;
  const live = placement.levels.filter((level) => level.state !== "CANCELLED");
  const bought = live.filter((level) => level.state === "FILLED" || level.state === "PARTIALLY_FILLED");
  return live.length === 0 ? null : `${String(bought.length)} из ${String(live.length)}`;
}

/** How far the price has to rise to the target. */
function goal(slot: SlotResponse): string {
  const outlook = slot.outlook;
  if (outlook?.trailing_sell_price != null) return `продажа не ниже ${slotQuote(slot)(outlook.trailing_sell_price)}`;
  return outlook?.to_target == null ? "цель —" : `цель ${signedPercent(outlook.to_target, 1)}`;
}

/** What buys next: the next rung, a reversal after day 14, or a delisting. */
function next(slot: SlotResponse, now: number): string {
  if (slot.delisting_at != null) return delistingText(slot.delisting_at, new Date(now));
  const outlook = slot.outlook;
  if (outlook == null) return "";
  if (outlook.to_next_rung != null) return `докупка ${signedPercent(outlook.to_next_rung, 1)}`;
  if (outlook.ladder_spent && outlook.recovery_from != null) {
    const from = new Date(outlook.recovery_from);
    return from.getTime() <= now
      ? "вторая сетка"
      : `вторая сетка с ${String(from.getUTCDate()).padStart(2, "0")}.${String(from.getUTCMonth() + 1).padStart(2, "0")}`;
  }
  return "";
}

/**
 * How to read the rail, drawn with the rail's own marks so the reader matches
 * a sign to its meaning rather than a word to a shape.
 */
function RailLegend(): React.JSX.Element {
  return (
    <ul
      className="flex flex-wrap gap-x-4 gap-y-1 border-b border-border-subtle px-3 py-2 text-helper-sm text-text-secondary"
      data-testid="rail-legend"
    >
      <li className="flex items-center gap-1.5">
        <span aria-hidden="true" className="size-2 rounded-full bg-chart-negative" />
        цена сейчас
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden="true" className="h-3 w-0.5 bg-text-primary" />
        средняя покупки
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden="true" className="size-2 rotate-45 bg-chart-positive" />
        цель выхода
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden="true" className="h-1 w-4 rounded-full bg-chart-positive/40" />
        сколько цене осталось до цели
      </li>
      <li className="flex items-center gap-1.5">
        <span aria-hidden="true" className="h-2.5 w-px bg-text-secondary" />
        ордера сетки
      </li>
    </ul>
  );
}

/** The money in one line: what the account is worth and where it is. */
export function MoneyLine({ overview }: { readonly overview: OverviewResponse }): React.JSX.Element {
  const figures: ReadonlyArray<readonly [string, string, string]> = [
    ["Баланс", money(overview.equity), "text-text-primary"],
    ["Свободно", money(overview.strategy_free_capital), "text-text-primary"],
    ["В позициях", money(overview.position_margin), "text-text-primary"],
    ["Резерв докупок", money(overview.reserved_grid_capital), "text-text-primary"],
    ["Открытые сейчас", signedMoney(overview.unrealised), tone(overview.unrealised)],
  ];
  return (
    <dl
      className="grid grid-cols-2 gap-px overflow-hidden rounded-panel border border-border-subtle bg-border-subtle sm:grid-cols-6 lg:grid-cols-5"
      data-testid="terminal-money"
    >
      {figures.map(([label, value, colour], index) => (
        // Five figures fill every row: the last spans two on a phone, and on
        // a tablet three sit above two wider ones.
        <div
          key={label}
          className={`flex min-w-0 flex-col gap-0.5 bg-surface-default px-4 py-3 lg:col-span-1 ${
            index < 3 ? "sm:col-span-2" : "sm:col-span-3"
          } ${index === figures.length - 1 ? "max-sm:col-span-2" : ""}`}
        >
          <dt className="text-helper-sm text-text-muted">{label}, USDT</dt>
          <dd className={`truncate font-numeric text-metric-md tabular-nums ${colour}`}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function CycleListRow({
  slot,
  selected,
  now,
  onSelect,
}: {
  readonly slot: SlotResponse;
  readonly selected: boolean;
  readonly now: number;
  readonly onSelect: () => void;
}): React.JSX.Element {
  const pool = logicalPoolOf(slot) ?? "—";
  const result = returnOf(slot)?.value ?? null;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={`terminal-row-${slot.slot_id}`}
      className={`block w-full cursor-pointer border-b border-border-subtle px-3 py-2.5 text-left transition-colors duration-150 last:border-b-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring ${
        selected ? "bg-surface-selected" : "hover:bg-surface-hover"
      }`}
    >
      <span className="flex items-baseline gap-2">
        <span aria-hidden="true" className={`size-2 shrink-0 self-center rounded-full ${poolColour(pool)}`} />
        <span className="min-w-0 flex-1 truncate font-numeric text-body-main text-text-primary">{slot.symbol}</span>
        {trailing(slot) && (
          <span title="Позиция в плюсе: бот ведёт цену вверх и продаст на развороте" data-testid={`terminal-trailing-${slot.slot_id}`}>
            <StatusBadge tone="ready">трейлинг</StatusBadge>
          </span>
        )}
        <span className="text-helper-sm text-text-muted">{pool}</span>
        <span className={`w-20 text-right font-numeric text-body-main tabular-nums ${tone(result)}`}>
          {result === null ? "—" : signedPercent(result, 1)}
        </span>
      </span>
      <span className="mt-2 block px-0.5">
        <CycleRail slot={slot} testId={`terminal-rail-${slot.slot_id}`} />
      </span>
      <span className="mt-1.5 grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto] gap-x-3 text-helper-sm tabular-nums text-text-secondary">
        <span className="truncate">{goal(slot)}</span>
        <span className="truncate">{next(slot, now)}</span>
        <span className="w-14 text-right">{ageOf(slot, now)}</span>
      </span>
    </button>
  );
}

/** What the selected cycle is, under its chart: the prices the chart tags. */
function CycleFacts({ slot, now, simple }: {
  readonly slot: SlotResponse;
  readonly now: number;
  readonly simple: boolean;
}): React.JSX.Element {
  const q = slotQuote(slot);
  const target = slot.outlook?.target_price ?? slot.exit_arm_price ?? null;
  const rung = slot.outlook?.next_rung_price ?? null;
  const all: ReadonlyArray<readonly [string, string]> = [
    ["Средняя", slot.vwap == null ? "—" : q(slot.vwap)],
    ["Цель", target === null ? "—" : q(target)],
    ["Следующая докупка", rung !== null ? q(rung) : slot.outlook?.ladder_spent === true ? "сетка куплена" : "—"],
    ["Сетка", ladder(slot) ?? "—"],
    ["Маржа, USDT", money(slot.margin)],
    ["Открыт", `${ageOf(slot, now)} назад`],
  ];
  // A client reads prices and money, not the ladder's bookkeeping.
  const facts = simple ? all.filter(([label]) => label !== "Сетка") : all;
  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 xl:grid-cols-6" data-testid="terminal-facts">
      {facts.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-helper-sm text-text-muted">{label}</dt>
          <dd className="truncate font-numeric text-body-main tabular-nums text-text-primary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ChartPane({
  slot,
  simple,
  now,
  onBack,
}: {
  readonly slot: SlotResponse;
  readonly simple: boolean;
  readonly now: number;
  readonly onBack?: () => void;
}): React.JSX.Element {
  const pool = logicalPoolOf(slot) ?? "—";
  const result = returnOf(slot)?.value ?? null;
  return (
    <section
      aria-label={`График ${slot.symbol ?? ""}`}
      className="min-w-0 rounded-panel border border-border-subtle bg-surface-default p-4"
      data-testid="terminal-chart"
    >
      <header className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {onBack !== undefined && (
          <div className="w-full">
            <Button variant="ghost" size="sm" onClick={onBack}>
              Назад к списку
            </Button>
          </div>
        )}
        <h2 className="font-numeric text-metric-md text-text-primary">{slot.symbol}</h2>
        <span className="flex items-center gap-1.5 text-body-main text-text-secondary">
          <span aria-hidden="true" className={`size-2 rounded-full ${poolColour(pool)}`} />
          {pool}
        </span>
        <span className={`ml-auto font-numeric text-metric-md tabular-nums ${tone(result)}`}>
          {result === null ? "—" : signedPercent(result, 2)}
        </span>
        <span className={`font-numeric text-body-main tabular-nums ${tone(slot.unrealised)}`}>
          {signedMoney(slot.unrealised)} USDT
        </span>
      </header>
      <CycleChart key={slot.slot_id} slot={slot} simple={simple} large />
      <CycleFacts slot={slot} now={now} simple={simple} />
    </section>
  );
}

function useWide(): boolean {
  const query = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => typeof window === "undefined" || window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = (): void => {
      setWide(media.matches);
    };
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
    };
  }, []);
  return wide;
}

export function TradesTerminal({
  overview,
  simple = false,
}: {
  readonly overview: OverviewResponse;
  /** The client's terminal: no ladder on the chart, plain words. */
  readonly simple?: boolean;
}): React.JSX.Element {
  const now = Date.now();
  const wide = useWide();
  const [filter, setFilter] = useState<Filter>("ALL");
  const open = useMemo(() => overview.cycles.filter((slot) => slot.symbol !== null && slot.opened_at !== null), [overview.cycles]);
  const counts = useMemo(() => {
    const result: Record<Filter, number> = { ALL: open.length, PRIME: 0, PULSE: 0, FLASH: 0, TRAILING: 0, DOWN: 0 };
    for (const slot of open) {
      const pool = logicalPoolOf(slot);
      if (pool === "PRIME" || pool === "PULSE" || pool === "FLASH") result[pool] += 1;
      if (isNegative(slot.unrealised)) result.DOWN += 1;
      if (trailing(slot)) result.TRAILING += 1;
    }
    return result;
  }, [open]);
  // Worst first: the cycle that most needs looking at leads the list.
  const shown = useMemo(
    () =>
      open
        .filter((slot) =>
          filter === "ALL"
            ? true
            : filter === "DOWN"
              ? isNegative(slot.unrealised)
              : filter === "TRAILING"
                ? trailing(slot)
                : logicalPoolOf(slot) === filter,
        )
        .sort((a, b) => compareDecimal(returnOf(a)?.value ?? "0", returnOf(b)?.value ?? "0")),
    [open, filter],
  );
  const [chosen, setChosen] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  const selected = shown.find((slot) => slot.slot_id === chosen) ?? (wide ? shown[0] : undefined) ?? null;

  const list = (
    <div className="flex min-w-0 flex-col rounded-panel border border-border-subtle bg-surface-default">
      <div role="group" aria-label="Какие циклы показать" className="flex flex-wrap gap-1 border-b border-border-subtle p-2">
        {FILTERS.map(([id, label]) => (
          <Button
            key={id}
            variant="ghost"
            size="sm"
            selected={filter === id}
            aria-pressed={filter === id}
            onClick={() => {
              setFilter(id);
            }}
          >
            {label} <span className="tabular-nums text-text-muted">{counts[id]}</span>
          </Button>
        ))}
      </div>
      <RailLegend />
      {shown.length === 0 ? (
        <p className="p-4 text-body-main text-text-secondary">
          {open.length === 0 ? "Открытых циклов сейчас нет. Бот откроет новый, когда монета пройдёт отбор." : "В этом фильтре циклов нет."}
        </p>
      ) : (
        <div className="max-h-[38rem] overflow-y-auto overscroll-contain" data-testid="terminal-list">
          {shown.map((slot) => (
            <CycleListRow
              key={slot.slot_id}
              slot={slot}
              now={now}
              selected={selected?.slot_id === slot.slot_id}
              onSelect={() => {
                setChosen(slot.slot_id);
                if (!wide) setSheet(true);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="trades-terminal">
      <MoneyLine overview={overview} />
      {wide ? (
        <div className="grid min-w-0 grid-cols-12 items-start gap-3">
          <div className="col-span-8 min-w-0">
            {selected === null ? (
              <p className="rounded-panel border border-border-subtle bg-surface-default p-6 text-body-main text-text-secondary">
                Выберите цикл в списке справа.
              </p>
            ) : (
              <ChartPane slot={selected} simple={simple} now={now} />
            )}
          </div>
          <div className="col-span-4 min-w-0">{list}</div>
        </div>
      ) : sheet && selected !== null ? (
        <ChartPane
          slot={selected}
          simple={simple}
          now={now}
          onBack={() => {
            setSheet(false);
          }}
        />
      ) : (
        list
      )}
    </div>
  );
}
